import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatDateOnlyYYYYMMDD } from "../utils/dateOnly";
import InfoTooltip from "../components/common/InfoTooltip";
import ModalCloseX from "../components/common/ModalCloseX";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import DeleteTicketModal from "../components/modals/DeleteTicketModal";
import {
  acknowledgeModule,
  createTrainingCategory,
  createTrainingLesson,
  createTrainingModule,
  deleteTrainingCategory,
  deleteTrainingLesson,
  deleteLessonDocument,
  getTrainingLessonDocumentBlob,
  getMyTrainingDashboard,
  getTrainingAdminOverview,
  getTrainingModuleDetail,
  getTrainingSettings,
  listTrainingAssignments,
  listTrainingUsers,
  listTrainingCategories,
  listTrainingModules,
  publishTrainingModule,
  setLessonComplete,
  submitModuleQuiz,
  updateTrainingAssignment,
  unpublishTrainingModule,
  updateTrainingCategory,
  updateTrainingLesson,
  updateTrainingModule,
  updateTrainingSettings,
  uploadLessonFile,
  upsertTrainingAssignment,
  deleteTrainingAssignment,
} from "../api/training";
import { TRAINING_TOOLTIPS } from "../constants/trainingTooltips";
import "./HrSectionCompact.css";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "not_started", label: "Not started" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
];

const ROLE_OPTIONS = [
  { value: "technician", label: "Technician" },
  { value: "receptionist", label: "Receptionist" },
  { value: "manager", label: "Manager" },
  { value: "owner", label: "Owner" },
  { value: "apprentice", label: "Apprentice" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
];

const LESSON_TYPE_OPTIONS = [
  { value: "video", label: "Video" },
  { value: "pdf", label: "Document" },
  { value: "link", label: "External link" },
];

const ASSIGNMENT_TYPE_TABS = [
  { value: "all", label: "All Assignments" },
  { value: "role", label: "Role Assignments" },
  { value: "user", label: "User Assignments" },
];

const SETTINGS_OPTIONS = [
  {
    key: "training_pro_enabled",
    label: "Enable Training Pro",
    tooltip: TRAINING_TOOLTIPS.settings.proEnabled,
  },
  {
    key: "quizzes_enabled",
    label: "Enable quizzes",
    tooltip: TRAINING_TOOLTIPS.settings.quizzesEnabled,
  },
  {
    key: "certificates_enabled",
    label: "Enable certificates",
    tooltip: TRAINING_TOOLTIPS.settings.certificatesEnabled,
  },
  {
    key: "acknowledgements_enabled",
    label: "Enable SOP acknowledgements",
    tooltip: TRAINING_TOOLTIPS.settings.ackEnabled,
  },
];

const HELP_SECTIONS = [
  {
    id: "overview",
    title: "How to use this page (steps)",
    description: "Follow these steps to run training smoothly from one page.",
    anchorId: "td-overview",
    bullets: [
      "Step 1: Review Assigned to me to see current workload.",
      "Step 2: Use filters to find the right module quickly.",
      "Step 3: Open a module and complete lessons in order.",
      "Step 4: Managers can switch to Manage to update content and assignments.",
    ],
    tips: [
      "Keep module names short and clear so staff can find them fast.",
      "Use one standard process for onboarding modules across all new hires.",
    ],
  },
  {
    id: "assigned",
    title: "Assigned dashboard",
    description: "See progress totals and current assignment status.",
    anchorId: "td-assigned",
    bullets: [
      "Review in progress, completed, and not started counts.",
      "Overdue badge highlights urgent items.",
    ],
    tips: [
      "Check this section at the start of each shift.",
      "Follow up on overdue items first for best completion rates.",
    ],
  },
  {
    id: "filters-modules",
    title: "Filters and modules",
    description: "Narrow the module list and open the right module quickly.",
    anchorId: "td-filters",
    bullets: [
      "Filter by category and status.",
      "Search by module title or category.",
      "Select a module from the list to open details.",
    ],
    tips: [
      "If results look empty, clear all filters and try again.",
      "Use search with 1-2 key words instead of full sentences.",
    ],
  },
  {
    id: "workspace",
    title: "Training workspace",
    description: "Use My Training and Manage tabs to work in the right mode.",
    anchorId: "td-workspace",
    bullets: [
      "My Training focuses on assigned modules.",
      "Manage mode enables settings, content, and assignments.",
    ],
    tips: [
      "Keep daily learning in My Training to avoid accidental admin edits.",
      "Use Manage mode in planned update windows.",
    ],
  },
  {
    id: "manage-settings",
    title: "Create and maintain content",
    description: "Set up categories, modules, lessons, and feature switches.",
    anchorId: "td-manage-settings",
    bullets: [
      "Step 1: Create categories for clean organization.",
      "Step 2: Create modules with clear titles and short descriptions.",
      "Step 3: Add lessons (video, document, or link) and sort them.",
      "Step 4: Publish only when content is fully reviewed.",
    ],
    tips: [
      "Use 3-8 lessons per module for easier completion.",
      "Update older modules when salon standards change.",
    ],
  },
  {
    id: "assignments",
    title: "Assignments",
    description: "Assign modules by role or user and maintain existing assignments.",
    anchorId: "td-manage-assignments",
    bullets: [
      "Set assigned dates, due dates, and priority.",
      "Use filters to review existing assignments quickly.",
    ],
    tips: [
      "Assign by role for consistency; assign by user only for exceptions.",
      "Set realistic due dates to improve completion and reduce overdue items.",
    ],
  },
];

const TROUBLESHOOTING_ITEMS = [
  {
    title: "Module not visible",
    body: "Check filters and make sure the module is assigned or published.",
  },
  {
    title: "Assignment not updating",
    body: "Confirm assigned and due dates, then save again.",
  },
  {
    title: "Cannot manage training",
    body: "Manage mode requires owner or manager permissions.",
  },
];

export default function TrainingDevelopment() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const location = useLocation();
  const moduleIdParam = useMemo(() => {
    const value = new URLSearchParams(location.search || "").get("moduleId");
    if (!value) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }, [location.search]);
  const [dashboard, setDashboard] = useState(null);
  const [moduleDetail, setModuleDetail] = useState(null);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [loadingModule, setLoadingModule] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    category: "",
    status: "",
    search: "",
  });
  const [activeTab, setActiveTab] = useState("my");
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizStatus, setQuizStatus] = useState("");
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [moduleParamApplied, setModuleParamApplied] = useState(false);

  const [manageCategories, setManageCategories] = useState([]);
  const [manageModules, setManageModules] = useState([]);
  const [manageSettings, setManageSettings] = useState(null);
  const [manageUsers, setManageUsers] = useState([]);
  const [manageOverview, setManageOverview] = useState(null);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageError, setManageError] = useState("");
  const [manageNotice, setManageNotice] = useState("");
  const [manageAssignments, setManageAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsError, setAssignmentsError] = useState("");
  const [assignmentFilters, setAssignmentFilters] = useState({
    type: "all",
    role: "",
    module_id: "",
    search: "",
  });
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [assignmentDraft, setAssignmentDraft] = useState({
    assigned_on: "",
    due_on: "",
    priority: "normal",
    is_required: true,
  });
  const [categoryDraft, setCategoryDraft] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");
  const [moduleDraft, setModuleDraft] = useState({
    title: "",
    description: "",
    category_id: "",
  });
  const [selectedManageModuleId, setSelectedManageModuleId] = useState(null);
  const [manageModuleDetail, setManageModuleDetail] = useState(null);
  const [moduleSaving, setModuleSaving] = useState(false);
  const [lessonDraft, setLessonDraft] = useState({
    title: "",
    lesson_type: "video",
    video_url: "",
    external_url: "",
    file: null,
    sort_order: "",
    estimated_minutes: "",
  });
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [assignmentRoleDraft, setAssignmentRoleDraft] = useState({
    module_id: "",
    target_role: "technician",
    due_on: "",
    priority: "normal",
  });
  const [assignmentUserDraft, setAssignmentUserDraft] = useState({
    module_id: "",
    target_user_id: "",
    due_on: "",
    priority: "normal",
  });
  const [removeDocConfirmOpen, setRemoveDocConfirmOpen] = useState(false);
  const [removeDocLessonId, setRemoveDocLessonId] = useState(null);
  const [removeDocSubmitting, setRemoveDocSubmitting] = useState(false);
  const [removeDocError, setRemoveDocError] = useState("");
  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const highlightTimeoutRef = useRef(null);

  const assignedModules = Array.isArray(dashboard?.assigned_modules)
    ? dashboard.assigned_modules
    : [];
  const categoryOptions = Array.isArray(dashboard?.categories)
    ? dashboard.categories
    : [];
  const roleValue = (user?.role || "").toLowerCase();
  const canManageTraining =
    Boolean(dashboard?.can_manage) || ["owner", "manager"].includes(roleValue);

  const filteredModules = useMemo(() => {
    const searchTerm = (filters.search || "").trim().toLowerCase();
    return assignedModules.filter((module) => {
      if (filters.category && String(module.category_id) !== filters.category) {
        return false;
      }
      if (filters.status && module.status !== filters.status) {
        return false;
      }
      if (!searchTerm) return true;
      const haystack = `${module.title || ""} ${module.category_name || ""}`.toLowerCase();
      return haystack.includes(searchTerm);
    });
  }, [assignedModules, filters]);

  const lessons = Array.isArray(moduleDetail?.lessons) ? moduleDetail.lessons : [];
  const lessonProgressMap = useMemo(() => {
    const map = new Map();
    (moduleDetail?.lesson_progress || []).forEach((row) => {
      if (row?.lesson_id != null) {
        map.set(row.lesson_id, row);
      }
    });
    return map;
  }, [moduleDetail?.lesson_progress]);

  const moduleProgress = moduleDetail?.progress || null;
  const quizQuestions = Array.isArray(moduleDetail?.quiz_questions)
    ? moduleDetail.quiz_questions
    : [];
  const selectedAssignment = useMemo(
    () => assignedModules.find((item) => item.module_id === selectedModuleId) || null,
    [assignedModules, selectedModuleId]
  );
  const editingLesson = useMemo(() => {
    if (!editingLessonId || !manageModuleDetail?.lessons) return null;
    return (
      manageModuleDetail.lessons.find((lesson) => lesson.id === editingLessonId) ||
      null
    );
  }, [editingLessonId, manageModuleDetail?.lessons]);

  const filteredAssignments = useMemo(() => {
    const query = (assignmentFilters.search || "").trim().toLowerCase();
    return (manageAssignments || []).filter((assignment) => {
      if (assignmentFilters.type !== "all") {
        if (assignment.target_type !== assignmentFilters.type) return false;
      }
      if (assignmentFilters.role) {
        if ((assignment.target_role || "") !== assignmentFilters.role) return false;
      }
      if (assignmentFilters.module_id) {
        if (String(assignment.module_id) !== assignmentFilters.module_id) return false;
      }
      if (!query) return true;
      const haystack = `${assignment.module_title || ""} ${
        assignment.target_user_name || ""
      } ${assignment.target_role || ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [manageAssignments, assignmentFilters]);

  const loadDashboard = useCallback(async () => {
    if (!token) return;
    setLoadingDashboard(true);
    setError("");
    try {
      const data = await getMyTrainingDashboard(token);
      setDashboard(data);
      if (
        !moduleIdParam &&
        !selectedModuleId &&
        Array.isArray(data?.assigned_modules) &&
        data.assigned_modules.length
      ) {
        setSelectedModuleId(data.assigned_modules[0].module_id);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load training assignments.");
    } finally {
      setLoadingDashboard(false);
    }
  }, [token, selectedModuleId, moduleIdParam]);

  const loadModuleDetail = useCallback(
    async (moduleId) => {
      if (!moduleId || !token) {
        setModuleDetail(null);
        return;
      }
      setLoadingModule(true);
      setError("");
      try {
        const data = await getTrainingModuleDetail(moduleId, token);
        setModuleDetail(data);
        setQuizAnswers({});
        setQuizStatus("");
      } catch (err) {
        setModuleDetail(null);
        setError(err?.response?.data?.detail || "Unable to load module details.");
      } finally {
        setLoadingModule(false);
      }
    },
    [token]
  );

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (!canManageTraining && activeTab !== "my") {
      setActiveTab("my");
    }
  }, [canManageTraining, activeTab]);

  useEffect(() => {
    setModuleParamApplied(false);
  }, [moduleIdParam]);

  useEffect(() => {
    if (!moduleIdParam || moduleParamApplied || !dashboard) return;
    const assigned = assignedModules.some(
      (module) => Number(module.module_id) === moduleIdParam
    );
    if (assigned) {
      setActiveTab("my");
      setSelectedModuleId(moduleIdParam);
    } else if (dashboard?.can_manage || canManageTraining) {
      setActiveTab("manage");
      setSelectedManageModuleId(moduleIdParam);
    } else {
      setSelectedModuleId(moduleIdParam);
    }
    setModuleParamApplied(true);
  }, [
    moduleIdParam,
    moduleParamApplied,
    dashboard,
    assignedModules,
    setSelectedManageModuleId,
    canManageTraining,
  ]);

  useEffect(() => {
    if (!selectedModuleId) return;
    loadModuleDetail(selectedModuleId);
  }, [selectedModuleId, loadModuleDetail]);

  const refreshAfterAction = async () => {
    await loadDashboard();
    if (selectedModuleId) {
      await loadModuleDetail(selectedModuleId);
    }
  };

  const handleLessonToggle = async (lessonId, completed) => {
    if (!lessonId) return;
    try {
      await setLessonComplete(lessonId, completed, token);
      await refreshAfterAction();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update lesson progress.");
    }
  };

  const handleAcknowledge = async () => {
    if (!moduleDetail?.module?.id) return;
    try {
      await acknowledgeModule(moduleDetail.module.id, token);
      await loadModuleDetail(moduleDetail.module.id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save acknowledgement.");
    }
  };

  const handleSubmitQuiz = async () => {
    if (!moduleDetail?.module?.id || !quizQuestions.length) return;
    setQuizSubmitting(true);
    setQuizStatus("");
    try {
      const result = await submitModuleQuiz(
        moduleDetail.module.id,
        { answers: quizAnswers },
        token
      );
      setQuizStatus(
        result?.passed ? `Passed (${result.score}%)` : `Score: ${result.score}%`
      );
      await loadModuleDetail(moduleDetail.module.id);
    } catch (err) {
      setQuizStatus(err?.response?.data?.detail || "Unable to submit quiz answers.");
    } finally {
      setQuizSubmitting(false);
    }
  };

  const startManageMode = useCallback(async () => {
    if (!token || !canManageTraining) return;
    setManageLoading(true);
    setManageError("");
    setManageNotice("");
    setAssignmentsError("");
    setAssignmentsLoading(true);
    try {
      const [categories, modules, settings, users, assignments] = await Promise.all([
        listTrainingCategories(token),
        listTrainingModules(token),
        getTrainingSettings(token),
        listTrainingUsers(token),
        listTrainingAssignments({}, token),
      ]);
      setManageCategories(Array.isArray(categories) ? categories : []);
      setManageModules(Array.isArray(modules) ? modules : []);
      setManageSettings(settings || null);
      setManageUsers(Array.isArray(users) ? users : []);
      setManageAssignments(Array.isArray(assignments) ? assignments : []);
      if (!selectedManageModuleId && Array.isArray(modules) && modules.length) {
        setSelectedManageModuleId(modules[0].id);
      }
    } catch (err) {
      setManageError(err?.response?.data?.detail || "Unable to load manage data.");
    } finally {
      setManageLoading(false);
      setAssignmentsLoading(false);
    }
  }, [token, canManageTraining, selectedManageModuleId]);

  const loadManageModuleDetail = useCallback(
    async (moduleId) => {
      if (!moduleId || !token) {
        setManageModuleDetail(null);
        return;
      }
      try {
        const data = await getTrainingModuleDetail(moduleId, token);
        setManageModuleDetail(data);
      } catch (err) {
        setManageModuleDetail(null);
        setManageError(err?.response?.data?.detail || "Unable to load module.");
      }
    },
    [token]
  );

  const loadAssignments = useCallback(async () => {
    if (!token || !canManageTraining) return;
    setAssignmentsLoading(true);
    setAssignmentsError("");
    try {
      const data = await listTrainingAssignments({}, token);
      setManageAssignments(Array.isArray(data) ? data : []);
    } catch (err) {
      setAssignmentsError(
        err?.response?.data?.detail || "Unable to load assignments."
      );
    } finally {
      setAssignmentsLoading(false);
    }
  }, [token, canManageTraining]);

  useEffect(() => {
    if (activeTab === "manage") {
      startManageMode();
    }
  }, [activeTab, startManageMode]);

  useEffect(() => {
    if (!selectedManageModuleId) return;
    loadManageModuleDetail(selectedManageModuleId);
  }, [selectedManageModuleId, loadManageModuleDetail]);

  useEffect(() => {
    if (!selectedManageModuleId) return;
    const selected = manageModules.find((item) => item.id === selectedManageModuleId);
    if (!selected) return;
    setModuleDraft({
      title: selected.title || "",
      description: selected.description || "",
      category_id: selected.category_id ? String(selected.category_id) : "",
    });
  }, [selectedManageModuleId, manageModules]);

  const handleCreateCategory = async () => {
    const name = categoryDraft.trim();
    if (!name) return;
    try {
      const created = await createTrainingCategory({ name }, token);
      setManageCategories((prev) => [...prev, created]);
      setCategoryDraft("");
    } catch (err) {
      setManageError(err?.response?.data?.detail || "Unable to create category.");
    }
  };

  const handleSaveCategory = async (categoryId) => {
    if (!editingCategoryName.trim()) return;
    try {
      const updated = await updateTrainingCategory(
        categoryId,
        { name: editingCategoryName.trim() },
        token
      );
      setManageCategories((prev) =>
        prev.map((item) => (item.id === categoryId ? updated : item))
      );
      setEditingCategoryId(null);
      setEditingCategoryName("");
    } catch (err) {
      setManageError(err?.response?.data?.detail || "Unable to update category.");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!categoryId) return;
    try {
      await deleteTrainingCategory(categoryId, token);
      setManageCategories((prev) => prev.filter((item) => item.id !== categoryId));
    } catch (err) {
      setManageError(err?.response?.data?.detail || "Unable to delete category.");
    }
  };

  const handleCreateModule = async () => {
    const title = moduleDraft.title.trim();
    if (!title) return;
    setModuleSaving(true);
    try {
      const payload = {
        title,
        description: moduleDraft.description || null,
        category_id: moduleDraft.category_id ? Number(moduleDraft.category_id) : null,
      };
      const created = await createTrainingModule(payload, token);
      setManageModules((prev) => [created, ...prev]);
      setModuleDraft({ title: "", description: "", category_id: "" });
      setSelectedManageModuleId(created.id);
    } catch (err) {
      setManageError(err?.response?.data?.detail || "Unable to create module.");
    } finally {
      setModuleSaving(false);
    }
  };

  const handleUpdateModule = async () => {
    if (!selectedManageModuleId) return;
    setModuleSaving(true);
    try {
      const payload = {
        title: moduleDraft.title.trim(),
        description: moduleDraft.description || null,
        category_id: moduleDraft.category_id ? Number(moduleDraft.category_id) : null,
      };
      const updated = await updateTrainingModule(selectedManageModuleId, payload, token);
      setManageModules((prev) =>
        prev.map((item) => (item.id === selectedManageModuleId ? updated : item))
      );
      await loadManageModuleDetail(selectedManageModuleId);
    } catch (err) {
      setManageError(err?.response?.data?.detail || "Unable to update module.");
    } finally {
      setModuleSaving(false);
    }
  };

  const handlePublishModule = async () => {
    if (!selectedManageModuleId) return;
    try {
      const updated = await publishTrainingModule(selectedManageModuleId, token);
      setManageModules((prev) =>
        prev.map((item) => (item.id === selectedManageModuleId ? updated : item))
      );
      await loadManageModuleDetail(selectedManageModuleId);
    } catch (err) {
      setManageError(err?.response?.data?.detail || "Unable to publish module.");
    }
  };

  const handleUnpublishModule = async () => {
    if (!selectedManageModuleId) return;
    try {
      const updated = await unpublishTrainingModule(selectedManageModuleId, token);
      setManageModules((prev) =>
        prev.map((item) => (item.id === selectedManageModuleId ? updated : item))
      );
      await loadManageModuleDetail(selectedManageModuleId);
    } catch (err) {
      setManageError(err?.response?.data?.detail || "Unable to unpublish module.");
    }
  };

  const handleCreateLesson = async () => {
    if (!selectedManageModuleId) return;
    const title = lessonDraft.title.trim();
    if (!title) return;
    try {
      const payload = {
        title,
        lesson_type: lessonDraft.lesson_type,
        video_url: lessonDraft.lesson_type === "video" ? lessonDraft.video_url : null,
        external_url: lessonDraft.lesson_type === "link" ? lessonDraft.external_url : null,
        file_path: null,
        sort_order: lessonDraft.sort_order ? Number(lessonDraft.sort_order) : null,
        estimated_minutes: lessonDraft.estimated_minutes
          ? Number(lessonDraft.estimated_minutes)
          : null,
      };
      const created = await createTrainingLesson(selectedManageModuleId, payload, token);
      if (lessonDraft.lesson_type === "pdf" && lessonDraft.file) {
        await uploadLessonFile(created.id, lessonDraft.file, token);
      }
      await loadManageModuleDetail(selectedManageModuleId);
      setLessonDraft({
        title: "",
        lesson_type: "video",
        video_url: "",
        external_url: "",
        file: null,
        sort_order: "",
        estimated_minutes: "",
      });
    } catch (err) {
      setManageError(err?.response?.data?.detail || "Unable to create lesson.");
    }
  };

  const handleUpdateLesson = async () => {
    if (!editingLessonId) return;
    const title = lessonDraft.title.trim();
    if (!title) return;
    try {
      const payload = {
        title,
        lesson_type: lessonDraft.lesson_type,
        video_url: lessonDraft.lesson_type === "video" ? lessonDraft.video_url : null,
        external_url: lessonDraft.lesson_type === "link" ? lessonDraft.external_url : null,
        file_path: manageModuleDetail?.lessons?.find((l) => l.id === editingLessonId)
          ?.file_path,
        sort_order: lessonDraft.sort_order ? Number(lessonDraft.sort_order) : null,
        estimated_minutes: lessonDraft.estimated_minutes
          ? Number(lessonDraft.estimated_minutes)
          : null,
      };
      await updateTrainingLesson(editingLessonId, payload, token);
      if (lessonDraft.lesson_type === "pdf" && lessonDraft.file) {
        await uploadLessonFile(editingLessonId, lessonDraft.file, token);
      }
      await loadManageModuleDetail(selectedManageModuleId);
      setEditingLessonId(null);
      setLessonDraft({
        title: "",
        lesson_type: "video",
        video_url: "",
        external_url: "",
        file: null,
        sort_order: "",
        estimated_minutes: "",
      });
    } catch (err) {
      setManageError(err?.response?.data?.detail || "Unable to update lesson.");
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!lessonId) return;
    try {
      await deleteTrainingLesson(lessonId, token);
      await loadManageModuleDetail(selectedManageModuleId);
    } catch (err) {
      setManageError(err?.response?.data?.detail || "Unable to delete lesson.");
    }
  };

  const handleOpenLessonDocument = async (lesson) => {
    if (!lesson?.id) return;
    const setDocError = activeTab === "manage" ? setManageError : setError;
    setDocError("");
    let previewWindow = null;
    let objectUrl = null;
    try {
      previewWindow = window.open("about:blank", "_blank");
    } catch (err) {
      previewWindow = null;
    }
    if (!previewWindow) {
      setDocError(
        "Could not open document. Please re-upload the file or contact your manager."
      );
      return;
    }
    try {
      const titleText = String(
        lesson?.document?.name || lesson?.title || "Document.pdf"
      );
      const safeTitle = titleText
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
      const safeFilename = titleText.replace(/["<>]/g, "") || "Document.pdf";

      previewWindow.document.open();
      previewWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${safeTitle}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 0; }
      .loading {
        padding: 24px;
        color: #475569;
        font-size: 14px;
      }
    </style>
  </head>
  <body>
    <div class="loading">Loading document...</div>
  </body>
</html>`);
      previewWindow.document.close();

      const blob = await getTrainingLessonDocumentBlob(lesson.id, token);
      if (!blob || blob.size <= 0) {
        throw new Error("empty_document_blob");
      }
      if (process.env.NODE_ENV !== "production") {
        console.debug("training.document.blob", {
          type: blob.type,
          size: blob.size,
        });
      }
      objectUrl = URL.createObjectURL(blob);

      previewWindow.document.open();
      previewWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${safeTitle}</title>
    <style>
      html, body { height: 100%; margin: 0; }
      iframe {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 0;
      }
      .fallback {
        position: fixed;
        right: 16px;
        top: 12px;
        font-size: 12px;
        background: rgba(255,255,255,0.9);
        padding: 6px 10px;
        border-radius: 999px;
        border: 1px solid #e2e8f0;
      }
      .fallback a { color: #0f172a; text-decoration: none; }
    </style>
  </head>
  <body>
    <div class="fallback">
      <a href="${objectUrl}" download="${safeFilename}">Download</a>
    </div>
    <iframe src="${objectUrl}" title="${safeTitle}"></iframe>
  </body>
</html>`);
      previewWindow.document.close();

      try {
        previewWindow.opener = null;
      } catch (err) {
        // no-op
      }
      setTimeout(() => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      }, 60 * 1000);
    } catch (err) {
      if (previewWindow) {
        previewWindow.close();
      }
      if (process.env.NODE_ENV !== "production") {
        const status = err?.response?.status;
        console.debug("training.document.error", status || err);
      }
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      setDocError(
        "Could not open document. Please re-upload the file or contact your manager."
      );
    }
  };

  const handleRemoveLessonDocument = (lesson) => {
    if (!lesson?.id) return;
    setRemoveDocLessonId(lesson.id);
    setRemoveDocError("");
    setRemoveDocConfirmOpen(true);
  };

  const handleConfirmRemoveLessonDocument = async () => {
    if (!removeDocLessonId || removeDocSubmitting) return;
    setRemoveDocSubmitting(true);
    setRemoveDocError("");
    try {
      const updated = await deleteLessonDocument(removeDocLessonId, token);
      setManageModuleDetail((prev) => {
        if (!prev) return prev;
        const lessons = Array.isArray(prev.lessons) ? prev.lessons : [];
        return {
          ...prev,
          lessons: lessons.map((lesson) =>
            lesson.id === removeDocLessonId ? updated : lesson
          ),
        };
      });
      setLessonDraft((prev) => ({ ...prev, file: null }));
      setManageNotice("Document removed.");
      setRemoveDocConfirmOpen(false);
      setRemoveDocLessonId(null);
    } catch (err) {
      setRemoveDocError(err?.response?.data?.detail || "Unable to remove document.");
    } finally {
      setRemoveDocSubmitting(false);
    }
  };

  const handleUpsertRoleAssignment = async () => {
    if (!assignmentRoleDraft.module_id) return;
    try {
      await upsertTrainingAssignment(
        {
          module_id: Number(assignmentRoleDraft.module_id),
          target_type: "role",
          target_role: assignmentRoleDraft.target_role,
          due_on: assignmentRoleDraft.due_on || null,
          priority: assignmentRoleDraft.priority,
        },
        token
      );
      setAssignmentRoleDraft({
        module_id: "",
        target_role: "technician",
        due_on: "",
        priority: "normal",
      });
      setManageNotice("Assignment saved.");
      await loadAssignments();
    } catch (err) {
      setManageError(err?.response?.data?.detail || "Unable to assign module.");
    }
  };

  const handleUpsertUserAssignment = async () => {
    if (!assignmentUserDraft.module_id || !assignmentUserDraft.target_user_id) return;
    try {
      await upsertTrainingAssignment(
        {
          module_id: Number(assignmentUserDraft.module_id),
          target_type: "user",
          target_user_id: Number(assignmentUserDraft.target_user_id),
          due_on: assignmentUserDraft.due_on || null,
          priority: assignmentUserDraft.priority,
        },
        token
      );
      setAssignmentUserDraft({
        module_id: "",
        target_user_id: "",
        due_on: "",
        priority: "normal",
      });
      setManageNotice("Assignment saved.");
      await loadAssignments();
    } catch (err) {
      setManageError(err?.response?.data?.detail || "Unable to assign module.");
    }
  };

  const startEditAssignment = (assignment) => {
    setEditingAssignmentId(assignment.id);
    setAssignmentDraft({
      assigned_on: assignment.assigned_on || "",
      due_on: assignment.due_on || "",
      priority: assignment.priority || "normal",
      is_required:
        typeof assignment.is_required === "boolean" ? assignment.is_required : true,
    });
    setAssignmentsError("");
    setManageNotice("");
  };

  const cancelEditAssignment = () => {
    setEditingAssignmentId(null);
    setAssignmentDraft({
      assigned_on: "",
      due_on: "",
      priority: "normal",
      is_required: true,
    });
  };

  const handleSaveAssignment = async (assignment) => {
    if (!assignmentDraft.assigned_on) {
      setAssignmentsError("Assigned on date is required.");
      return;
    }
    if (
      manageSettings?.training_pro_enabled &&
      assignmentDraft.due_on &&
      assignmentDraft.due_on < assignmentDraft.assigned_on
    ) {
      setAssignmentsError("Due date must be on or after assigned date.");
      return;
    }
    try {
      const payload = {
        assigned_on: assignmentDraft.assigned_on,
        is_required: Boolean(assignmentDraft.is_required),
      };
      if (manageSettings?.training_pro_enabled) {
        payload.due_on = assignmentDraft.due_on || null;
        payload.priority = assignmentDraft.priority;
      }
      await updateTrainingAssignment(assignment.id, payload, token);
      setManageNotice("Assignment updated.");
      cancelEditAssignment();
      await loadAssignments();
    } catch (err) {
      setAssignmentsError(err?.response?.data?.detail || "Unable to update assignment.");
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    const confirmed = window.confirm(
      "Remove this assignment? Staff will no longer be required to complete this module."
    );
    if (!confirmed) return;
    try {
      await deleteTrainingAssignment(assignmentId, token);
      setManageNotice("Assignment removed.");
      await loadAssignments();
    } catch (err) {
      setAssignmentsError(err?.response?.data?.detail || "Unable to delete assignment.");
    }
  };

  const handleToggleSetting = async (key) => {
    if (!manageSettings) return;
    try {
      const updated = await updateTrainingSettings(
        { [key]: !manageSettings[key] },
        token
      );
      setManageSettings(updated);
      if (key === "training_pro_enabled") {
        const overview = await getTrainingAdminOverview({}, token);
        setManageOverview(overview);
      }
    } catch (err) {
      setManageError(err?.response?.data?.detail || "Unable to update settings.");
    }
  };

  const loadAdminOverview = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getTrainingAdminOverview({}, token);
      setManageOverview(data);
    } catch (err) {
      setManageOverview(null);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === "manage" && manageSettings?.training_pro_enabled) {
      loadAdminOverview();
    }
  }, [activeTab, manageSettings?.training_pro_enabled, loadAdminOverview]);

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    },
    []
  );

  const scrollToSection = useCallback((id) => {
    const target = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }, []);

  const jumpToSectionWithHighlight = useCallback(
    (id) => {
      const jumped = scrollToSection(id);
      if (!jumped) return;
      setHighlightedSectionId(id);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedSectionId((current) => (current === id ? "" : current));
      }, 1500);
    },
    [scrollToSection]
  );

  const getSectionClassName = useCallback(
    (id, baseClassName) =>
      `${baseClassName} ${
        highlightedSectionId === id
          ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
          : ""
      }`,
    [highlightedSectionId]
  );

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="hrSectionShell">
      <div className="hrSectionWide hr-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Training &amp; Development
            </h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className="px-6 py-6 space-y-6">
            <div id="td-overview">
              <ReportHelpCard
                title="Help Guide: Training & Development"
                subtitle="Step-by-step guide to complete training and manage modules with better results."
                sections={HELP_SECTIONS}
                troubleshooting={TROUBLESHOOTING_ITEMS}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
              <div className="space-y-6">
                <div
                  id="td-assigned"
                  className={getSectionClassName(
                    "td-assigned",
                    "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-slate-900">
                      Assigned to me
                    </h2>
                    {dashboard?.overdue_count > 0 && (
                      <span className="rounded-full bg-rose-100 px-2 py-1 text-xs font-semibold text-rose-700">
                        {dashboard.overdue_count} overdue
                      </span>
                    )}
                  </div>
                  <div className="mt-3 grid gap-2 text-sm text-slate-600">
                    <div className="flex items-center justify-between">
                      <span>In progress</span>
                      <span className="font-semibold text-slate-900">
                        {dashboard?.in_progress_count || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Completed</span>
                      <span className="font-semibold text-slate-900">
                        {dashboard?.completed_count || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Not started</span>
                      <span className="font-semibold text-slate-900">
                        {dashboard?.not_started_count || 0}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  id="td-filters"
                  className={getSectionClassName(
                    "td-filters",
                    "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-4"
                  )}
                >
                  <div className="grid gap-2">
                    <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                      <span>Category</span>
                      <InfoTooltip
                        text={TRAINING_TOOLTIPS.filters.category}
                        label="Category filter"
                      />
                    </label>
                    <select
                      value={filters.category}
                      onChange={(event) =>
                        setFilters((prev) => ({
                          ...prev,
                          category: event.target.value,
                        }))
                      }
                      className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm"
                    >
                      <option value="">All categories</option>
                      {categoryOptions.map((category) => (
                        <option key={category.id} value={String(category.id)}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid gap-2">
                    <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                      <span>Status</span>
                      <InfoTooltip
                        text={TRAINING_TOOLTIPS.filters.status}
                        label="Status filter"
                      />
                    </label>
                    <select
                      value={filters.status}
                      onChange={(event) =>
                        setFilters((prev) => ({
                          ...prev,
                          status: event.target.value,
                        }))
                      }
                      className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm"
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid gap-2">
                    <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                      <span>Search</span>
                      <InfoTooltip
                        text={TRAINING_TOOLTIPS.filters.search}
                        label="Search modules"
                      />
                    </label>
                    <input
                      value={filters.search}
                      onChange={(event) =>
                        setFilters((prev) => ({
                          ...prev,
                          search: event.target.value,
                        }))
                      }
                      placeholder="Search modules"
                      className="h-10 rounded-xl border border-slate-300 px-3 text-sm"
                    />
                  </div>
                </div>

                <div
                  id="td-modules"
                  className={getSectionClassName(
                    "td-modules",
                    "rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
                  )}
                >
                  <div className="bg-slate-50 px-4 py-2 text-xs uppercase tracking-wide text-slate-500">
                    Modules
                  </div>
                  {loadingDashboard ? (
                    <div className="px-4 py-4 text-sm text-slate-500">
                      Loading assignments...
                    </div>
                  ) : filteredModules.length === 0 ? (
                    <div className="px-4 py-4 text-sm text-slate-500">
                      No modules match these filters.
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-200">
                      {filteredModules.map((module) => {
                        const isSelected = module.module_id === selectedModuleId;
                        const statusLabel =
                          STATUS_OPTIONS.find((opt) => opt.value === module.status)
                            ?.label || "Not started";
                        return (
                          <button
                            key={module.module_id}
                            type="button"
                            onClick={() => setSelectedModuleId(module.module_id)}
                            className={`w-full text-left px-4 py-3 space-y-1 ${
                              isSelected ? "bg-rose-50" : "hover:bg-slate-50"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="text-sm font-semibold text-slate-900">
                                  {module.title}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {module.category_name || "Uncategorized"}
                                </div>
                              </div>
                              {module.is_overdue && (
                                <span className="rounded-full bg-rose-100 px-2 py-1 text-[10px] font-semibold text-rose-700">
                                  Overdue
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                              <span>{statusLabel}</span>
                              {module.due_on && (
                                <span>
                                  Due {formatDateOnlyYYYYMMDD(module.due_on)}
                                </span>
                              )}
                              {module.priority && dashboard?.training_pro_enabled && (
                                <span className="uppercase">{module.priority}</span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div
                id="td-workspace"
                className={getSectionClassName("td-workspace", "space-y-6 rounded-2xl")}
              >
                {canManageTraining && (
                  <div className="flex items-center gap-2">
                    {["my", "manage"].map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveTab(tab)}
                        className={`rounded-full px-4 py-2 text-sm font-semibold ${
                          activeTab === tab
                            ? "bg-slate-900 text-white"
                            : "border border-slate-300 text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        {tab === "my" ? "My Training" : "Manage"}
                      </button>
                    ))}
                  </div>
                )}

                {activeTab === "manage" && canManageTraining ? (
                  <div className="space-y-6">
                    <div
                      id="td-manage-settings"
                      className={getSectionClassName(
                        "td-manage-settings",
                        "rounded-2xl border border-slate-200 bg-white shadow-sm"
                      )}
                    >
                      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                        <div>
                          <div className="text-lg font-semibold text-slate-900">
                            Training Pro Settings
                          </div>
                          <div className="text-xs text-slate-500">
                            Toggle pro features per salon
                          </div>
                        </div>
                        {manageLoading && (
                          <span className="text-xs text-slate-500">Loading...</span>
                        )}
                      </div>
                      <div className="px-5 py-4 space-y-3 text-sm">
                        {manageSettings ? (
                          <>
                            {SETTINGS_OPTIONS.map((setting) => (
                              <label
                                key={setting.key}
                                className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-2"
                              >
                                <span className="inline-flex items-center gap-2">
                                  {setting.label}
                                  <InfoTooltip
                                    text={setting.tooltip}
                                    label={setting.label}
                                  />
                                </span>
                                <input
                                  type="checkbox"
                                  checked={Boolean(manageSettings?.[setting.key])}
                                  onChange={() => handleToggleSetting(setting.key)}
                                  className="h-4 w-4 text-emerald-600 border-slate-300 rounded"
                                />
                              </label>
                            ))}
                          </>
                        ) : (
                          <div className="text-slate-500">Loading settings...</div>
                        )}
                      </div>
                    </div>
                    {manageNotice && (
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                        {manageNotice}
                      </div>
                    )}
                    {manageError && (
                      <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                        {manageError}
                      </div>
                    )}
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
                      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="px-5 py-4 border-b border-slate-100">
                          <div className="text-lg font-semibold text-slate-900">
                            Categories
                          </div>
                        </div>
                        <div className="px-5 py-4 space-y-3">
                          <div className="flex gap-2 items-end">
                            <div className="flex-1 space-y-1">
                              <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                                <span>Category name</span>
                                <InfoTooltip
                                  text={TRAINING_TOOLTIPS.categories.name}
                                  label="Category name"
                                />
                              </label>
                              <input
                                value={categoryDraft}
                                onChange={(event) => setCategoryDraft(event.target.value)}
                                placeholder="New category name"
                                className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={handleCreateCategory}
                              className="h-10 px-4 rounded-full bg-slate-900 text-white text-sm font-semibold"
                            >
                              Add
                            </button>
                          </div>
                          <div className="space-y-2 text-sm">
                            {manageCategories.map((category) => (
                              <div
                                key={category.id}
                                className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2"
                              >
                                {editingCategoryId === category.id ? (
                                  <input
                                    value={editingCategoryName}
                                    onChange={(event) =>
                                      setEditingCategoryName(event.target.value)
                                    }
                                    className="h-8 flex-1 rounded-lg border border-slate-300 px-2 text-sm"
                                  />
                                ) : (
                                  <span className="text-slate-700">
                                    {category.name}
                                  </span>
                                )}
                                <div className="flex items-center gap-2">
                                  {editingCategoryId === category.id ? (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => handleSaveCategory(category.id)}
                                        className="text-xs font-semibold text-slate-700"
                                      >
                                        Save
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingCategoryId(null);
                                          setEditingCategoryName("");
                                        }}
                                        className="text-xs text-slate-500"
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingCategoryId(category.id);
                                          setEditingCategoryName(category.name);
                                        }}
                                        className="text-xs font-semibold text-slate-700"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteCategory(category.id)}
                                        className="text-xs font-semibold text-rose-600"
                                      >
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                            {manageCategories.length === 0 && (
                              <div className="text-xs text-slate-500">
                                No categories yet.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="px-5 py-4 border-b border-slate-100">
                          <div className="text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
                            Modules
                            <InfoTooltip
                              text={TRAINING_TOOLTIPS.modules.republishVersion}
                              label="Module versioning"
                            />
                          </div>
                        </div>
                        <div className="px-5 py-4 space-y-3">
                          <div className="space-y-2">
                            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                              <span>Title</span>
                              <InfoTooltip
                                text={TRAINING_TOOLTIPS.modules.title}
                                label="Module title"
                              />
                            </label>
                            <input
                              value={moduleDraft.title}
                              onChange={(event) =>
                                setModuleDraft((prev) => ({
                                  ...prev,
                                  title: event.target.value,
                                }))
                              }
                              placeholder="Module title"
                              className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                              <span>Category</span>
                              <InfoTooltip
                                text={TRAINING_TOOLTIPS.modules.category}
                                label="Module category"
                              />
                            </label>
                            <select
                              value={moduleDraft.category_id}
                              onChange={(event) =>
                                setModuleDraft((prev) => ({
                                  ...prev,
                                  category_id: event.target.value,
                                }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm"
                            >
                              <option value="">Uncategorized</option>
                              {manageCategories.map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                              <span>Description</span>
                              <InfoTooltip
                                text={TRAINING_TOOLTIPS.modules.description}
                                label="Module description"
                              />
                            </label>
                            <textarea
                              value={moduleDraft.description}
                              onChange={(event) =>
                                setModuleDraft((prev) => ({
                                  ...prev,
                                  description: event.target.value,
                                }))
                              }
                              rows={3}
                              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                              placeholder="Optional module description"
                            />
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={handleCreateModule}
                              disabled={moduleSaving}
                              className="h-10 px-4 rounded-full bg-slate-900 text-white text-sm font-semibold disabled:opacity-60"
                            >
                              Create
                            </button>
                            <button
                              type="button"
                              onClick={handleUpdateModule}
                              disabled={moduleSaving || !selectedManageModuleId}
                              className="h-10 px-4 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold disabled:opacity-60"
                            >
                              Save changes
                            </button>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={handlePublishModule}
                                disabled={!selectedManageModuleId}
                                className="h-10 px-4 rounded-full bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60"
                              >
                                Publish
                              </button>
                              <InfoTooltip
                                text={TRAINING_TOOLTIPS.modules.publish}
                                label="Publish module"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={handleUnpublishModule}
                              disabled={!selectedManageModuleId}
                              className="h-10 px-4 rounded-full border border-emerald-200 text-emerald-700 text-sm font-semibold disabled:opacity-60"
                            >
                              Unpublish
                            </button>
                          </div>
                          <div className="rounded-xl border border-slate-200 overflow-hidden text-sm">
                            <div className="bg-slate-50 px-3 py-2 uppercase tracking-wide text-xs text-slate-500">
                              All modules
                            </div>
                            <div className="divide-y divide-slate-200">
                              {manageModules.map((module) => (
                                <button
                                  key={module.id}
                                  type="button"
                                  onClick={() => setSelectedManageModuleId(module.id)}
                                  className={`w-full px-3 py-2 text-left ${
                                    selectedManageModuleId === module.id
                                      ? "bg-rose-50"
                                      : "hover:bg-slate-50"
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="font-semibold text-slate-800">
                                      {module.title}
                                    </span>
                                    <span className="text-[11px] uppercase text-slate-400">
                                      {module.is_published ? "Published" : "Draft"}
                                    </span>
                                  </div>
                                </button>
                              ))}
                              {manageModules.length === 0 && (
                                <div className="px-3 py-2 text-xs text-slate-500">
                                  No modules yet.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                        <div className="text-lg font-semibold text-slate-900">
                          Lessons
                        </div>
                        <span className="text-xs text-slate-500">
                          {selectedManageModuleId
                            ? `Module #${selectedManageModuleId}`
                            : "Select a module"}
                        </span>
                      </div>
                      <div className="px-5 py-4 space-y-4">
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-1">
                            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                              <span>Title</span>
                              <InfoTooltip
                                text={TRAINING_TOOLTIPS.lessons.title}
                                label="Lesson title"
                              />
                            </label>
                            <input
                              value={lessonDraft.title}
                              onChange={(event) =>
                                setLessonDraft((prev) => ({
                                  ...prev,
                                  title: event.target.value,
                                }))
                              }
                              placeholder="Lesson title"
                              className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                              <span>Type</span>
                              <InfoTooltip
                                text={TRAINING_TOOLTIPS.lessons.type}
                                label="Lesson type"
                              />
                            </label>
                            <select
                              value={lessonDraft.lesson_type}
                              onChange={(event) =>
                                setLessonDraft((prev) => ({
                                  ...prev,
                                  lesson_type: event.target.value,
                                }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm"
                            >
                              {LESSON_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          {lessonDraft.lesson_type === "video" && (
                            <div className="space-y-1 md:col-span-2">
                              <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                                <span>Video URL</span>
                                <InfoTooltip
                                  text={TRAINING_TOOLTIPS.lessons.videoUrl}
                                  label="Video URL"
                                />
                              </label>
                              <input
                                value={lessonDraft.video_url}
                                onChange={(event) =>
                                  setLessonDraft((prev) => ({
                                    ...prev,
                                    video_url: event.target.value,
                                  }))
                                }
                                placeholder="Video URL"
                                className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
                              />
                            </div>
                          )}
                          {lessonDraft.lesson_type === "link" && (
                            <div className="space-y-1 md:col-span-2">
                              <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                                <span>External URL</span>
                                <InfoTooltip
                                  text={TRAINING_TOOLTIPS.lessons.externalUrl}
                                  label="External URL"
                                />
                              </label>
                              <input
                                value={lessonDraft.external_url}
                                onChange={(event) =>
                                  setLessonDraft((prev) => ({
                                    ...prev,
                                    external_url: event.target.value,
                                  }))
                                }
                                placeholder="External URL"
                                className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
                              />
                            </div>
                          )}
                          {lessonDraft.lesson_type === "pdf" && (
                            <div className="space-y-2 md:col-span-2">
                              <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                                <span>Upload document</span>
                                <InfoTooltip
                                  text={TRAINING_TOOLTIPS.lessons.upload}
                                  label="Upload document"
                                />
                              </label>
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={(event) =>
                                  setLessonDraft((prev) => ({
                                    ...prev,
                                    file: event.target.files?.[0] || null,
                                  }))
                                }
                                className="text-sm"
                              />
                              {lessonDraft.file && (
                                <div className="text-xs text-slate-500">
                                  Selected file:{" "}
                                  <span className="font-semibold text-slate-700">
                                    {lessonDraft.file.name}
                                  </span>
                                </div>
                              )}
                              {editingLesson?.document?.url && (
                                <div className="rounded-xl border border-slate-200 px-3 py-2 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                  <div>
                                    <div className="text-[11px] uppercase tracking-wide text-slate-500">
                                      Current document
                                    </div>
                                    <div className="text-sm font-semibold text-slate-800">
                                      {editingLesson.document.name || "Document.pdf"}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                      Removing the document only detaches it from this
                                      lesson.
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap gap-2 text-xs">
                                    <button
                                      type="button"
                                      onClick={() => handleOpenLessonDocument(editingLesson)}
                                      className="rounded-full border border-slate-300 px-3 py-1 font-semibold text-slate-700 hover:bg-slate-50"
                                    >
                                      Open document
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRemoveLessonDocument(editingLesson)
                                      }
                                      className="rounded-full border border-rose-300 px-3 py-1 font-semibold text-rose-600 hover:bg-rose-50"
                                    >
                                      Remove document
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="space-y-1">
                            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                              <span>Sort order</span>
                              <InfoTooltip
                                text={TRAINING_TOOLTIPS.lessons.sortOrder}
                                label="Lesson sort order"
                              />
                            </label>
                            <input
                              value={lessonDraft.sort_order}
                              onChange={(event) =>
                                setLessonDraft((prev) => ({
                                  ...prev,
                                  sort_order: event.target.value,
                                }))
                              }
                              placeholder="Sort order"
                              className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                              <span>Minutes</span>
                              <InfoTooltip
                                text={TRAINING_TOOLTIPS.lessons.estimatedMinutes}
                                label="Estimated minutes"
                              />
                            </label>
                            <input
                              value={lessonDraft.estimated_minutes}
                              onChange={(event) =>
                                setLessonDraft((prev) => ({
                                  ...prev,
                                  estimated_minutes: event.target.value,
                                }))
                              }
                              placeholder="Minutes"
                              className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
                            />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {editingLessonId ? (
                            <button
                              type="button"
                              onClick={handleUpdateLesson}
                              className="h-10 px-4 rounded-full bg-slate-900 text-white text-sm font-semibold"
                            >
                              Update Lesson
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={handleCreateLesson}
                              className="h-10 px-4 rounded-full bg-slate-900 text-white text-sm font-semibold"
                            >
                              Add Lesson
                            </button>
                          )}
                          {editingLessonId && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditingLessonId(null);
                                setLessonDraft({
                                  title: "",
                                  lesson_type: "video",
                                  video_url: "",
                                  external_url: "",
                                  file: null,
                                  sort_order: "",
                                  estimated_minutes: "",
                                });
                              }}
                              className="h-10 px-4 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                        <div className="rounded-xl border border-slate-200 overflow-hidden text-sm">
                          <div className="bg-slate-50 px-3 py-2 uppercase tracking-wide text-xs text-slate-500">
                            Lesson list
                          </div>
                          <div className="divide-y divide-slate-200">
                            {Array.isArray(manageModuleDetail?.lessons) &&
                              manageModuleDetail.lessons.map((lesson) => (
                                <div
                                  key={lesson.id}
                                  className="px-3 py-2 flex items-center justify-between"
                                >
                                  <div>
                                    <div className="font-semibold text-slate-800">
                                      {lesson.title}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                      {lesson.lesson_type}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingLessonId(lesson.id);
                                        setLessonDraft({
                                          title: lesson.title,
                                          lesson_type: lesson.lesson_type,
                                          video_url: lesson.video_url || "",
                                          external_url: lesson.external_url || "",
                                          file: null,
                                          sort_order: lesson.sort_order || "",
                                          estimated_minutes: lesson.estimated_minutes || "",
                                        });
                                      }}
                                      className="font-semibold text-slate-700"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteLesson(lesson.id)}
                                      className="font-semibold text-rose-600"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              ))}
                            {(!manageModuleDetail?.lessons ||
                              manageModuleDetail.lessons.length === 0) && (
                              <div className="px-3 py-2 text-xs text-slate-500">
                                No lessons yet.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
                      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="px-5 py-4 border-b border-slate-100">
                          <div className="text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
                            Assign to Role
                            <InfoTooltip
                              text={TRAINING_TOOLTIPS.assignments.targetType}
                              label="Assign to role"
                            />
                          </div>
                        </div>
                        <div className="px-5 py-4 space-y-3">
                          <div className="space-y-1">
                            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                              <span>Role</span>
                              <InfoTooltip
                                text={TRAINING_TOOLTIPS.assignments.role}
                                label="Assign to role"
                              />
                            </label>
                            <select
                              value={assignmentRoleDraft.target_role}
                              onChange={(event) =>
                                setAssignmentRoleDraft((prev) => ({
                                  ...prev,
                                  target_role: event.target.value,
                                }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm"
                            >
                              {ROLE_OPTIONS.map((role) => (
                                <option key={role.value} value={role.value}>
                                  {role.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                              <span>Module</span>
                              <InfoTooltip
                                text={TRAINING_TOOLTIPS.assignments.module}
                                label="Assignment module"
                              />
                            </label>
                            <select
                              value={assignmentRoleDraft.module_id}
                              onChange={(event) =>
                                setAssignmentRoleDraft((prev) => ({
                                  ...prev,
                                  module_id: event.target.value,
                                }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm"
                            >
                              <option value="">Select module</option>
                              {manageModules.map((module) => (
                                <option key={module.id} value={module.id}>
                                  {module.title}
                                </option>
                              ))}
                            </select>
                          </div>
                          {manageSettings?.training_pro_enabled && (
                            <>
                              <div className="space-y-1">
                                <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                                  <span>Due date</span>
                                  <InfoTooltip
                                    text={TRAINING_TOOLTIPS.assignments.dueOn}
                                    label="Assignment due date"
                                  />
                                </label>
                                <input
                                  type="date"
                                  value={assignmentRoleDraft.due_on}
                                  onChange={(event) =>
                                    setAssignmentRoleDraft((prev) => ({
                                      ...prev,
                                      due_on: event.target.value,
                                    }))
                                  }
                                  className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                                  <span>Priority</span>
                                  <InfoTooltip
                                    text={TRAINING_TOOLTIPS.assignments.priority}
                                    label="Assignment priority"
                                  />
                                </label>
                                <select
                                  value={assignmentRoleDraft.priority}
                                  onChange={(event) =>
                                    setAssignmentRoleDraft((prev) => ({
                                      ...prev,
                                      priority: event.target.value,
                                    }))
                                  }
                                  className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm"
                                >
                                  {PRIORITY_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={handleUpsertRoleAssignment}
                            className="h-10 w-full rounded-full bg-slate-900 text-white text-sm font-semibold"
                          >
                            Assign role
                          </button>
                        </div>
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="px-5 py-4 border-b border-slate-100">
                          <div className="text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
                            Assign to User
                            <InfoTooltip
                              text={TRAINING_TOOLTIPS.assignments.targetType}
                              label="Assign to user"
                            />
                          </div>
                        </div>
                        <div className="px-5 py-4 space-y-3">
                          <div className="space-y-1">
                            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                              <span>User</span>
                              <InfoTooltip
                                text={TRAINING_TOOLTIPS.assignments.user}
                                label="Assign to user"
                              />
                            </label>
                            <select
                              value={assignmentUserDraft.target_user_id}
                              onChange={(event) =>
                                setAssignmentUserDraft((prev) => ({
                                  ...prev,
                                  target_user_id: event.target.value,
                                }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm"
                            >
                              <option value="">Select user</option>
                              {manageUsers.map((member) => (
                                <option key={member.user_id} value={member.user_id}>
                                  {member.name} ({member.role})
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                              <span>Module</span>
                              <InfoTooltip
                                text={TRAINING_TOOLTIPS.assignments.module}
                                label="Assignment module"
                              />
                            </label>
                            <select
                              value={assignmentUserDraft.module_id}
                              onChange={(event) =>
                                setAssignmentUserDraft((prev) => ({
                                  ...prev,
                                  module_id: event.target.value,
                                }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm"
                            >
                              <option value="">Select module</option>
                              {manageModules.map((module) => (
                                <option key={module.id} value={module.id}>
                                  {module.title}
                                </option>
                              ))}
                            </select>
                          </div>
                          {manageSettings?.training_pro_enabled && (
                            <>
                              <div className="space-y-1">
                                <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                                  <span>Due date</span>
                                  <InfoTooltip
                                    text={TRAINING_TOOLTIPS.assignments.dueOn}
                                    label="Assignment due date"
                                  />
                                </label>
                                <input
                                  type="date"
                                  value={assignmentUserDraft.due_on}
                                  onChange={(event) =>
                                    setAssignmentUserDraft((prev) => ({
                                      ...prev,
                                      due_on: event.target.value,
                                    }))
                                  }
                                  className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-2">
                                  <span>Priority</span>
                                  <InfoTooltip
                                    text={TRAINING_TOOLTIPS.assignments.priority}
                                    label="Assignment priority"
                                  />
                                </label>
                                <select
                                  value={assignmentUserDraft.priority}
                                  onChange={(event) =>
                                    setAssignmentUserDraft((prev) => ({
                                      ...prev,
                                      priority: event.target.value,
                                    }))
                                  }
                                  className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm"
                                >
                                  {PRIORITY_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={handleUpsertUserAssignment}
                            className="h-10 w-full rounded-full bg-slate-900 text-white text-sm font-semibold"
                          >
                            Assign user
                          </button>
                        </div>
                      </div>
                    </div>
                    <div
                      id="td-manage-assignments"
                      className={getSectionClassName(
                        "td-manage-assignments",
                        "rounded-2xl border border-slate-200 bg-white shadow-sm"
                      )}
                    >
                      <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
                        <div className="text-lg font-semibold text-slate-900">
                          Existing Assignments
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {ASSIGNMENT_TYPE_TABS.map((tab) => (
                            <button
                              key={tab.value}
                              type="button"
                              onClick={() =>
                                setAssignmentFilters((prev) => ({
                                  ...prev,
                                  type: tab.value,
                                }))
                              }
                              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                                assignmentFilters.type === tab.value
                                  ? "bg-slate-900 text-white"
                                  : "border border-slate-300 text-slate-700 hover:bg-slate-50"
                              }`}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="px-5 py-4 space-y-4">
                        <div className="grid gap-3 lg:grid-cols-[200px_220px_minmax(0,1fr)]">
                          <div className="space-y-1">
                            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                              Role
                            </label>
                            <select
                              value={assignmentFilters.role}
                              onChange={(event) =>
                                setAssignmentFilters((prev) => ({
                                  ...prev,
                                  role: event.target.value,
                                }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm"
                            >
                              <option value="">All roles</option>
                              {ROLE_OPTIONS.map((role) => (
                                <option key={role.value} value={role.value}>
                                  {role.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                              Module
                            </label>
                            <select
                              value={assignmentFilters.module_id}
                              onChange={(event) =>
                                setAssignmentFilters((prev) => ({
                                  ...prev,
                                  module_id: event.target.value,
                                }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm"
                            >
                              <option value="">All modules</option>
                              {manageModules.map((module) => (
                                <option key={module.id} value={module.id}>
                                  {module.title}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                              Search
                            </label>
                            <input
                              value={assignmentFilters.search}
                              onChange={(event) =>
                                setAssignmentFilters((prev) => ({
                                  ...prev,
                                  search: event.target.value,
                                }))
                              }
                              placeholder="Search by module or user"
                              className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
                            />
                          </div>
                        </div>
                        {assignmentsError && (
                          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
                            {assignmentsError}
                          </div>
                        )}
                        <div className="overflow-x-auto rounded-2xl border border-slate-200">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                              <tr>
                                <th className="px-3 py-2 text-left">Type</th>
                                <th className="px-3 py-2 text-left">Role / User</th>
                                <th className="px-3 py-2 text-left">Module</th>
                                <th className="px-3 py-2 text-left">Assigned On</th>
                                <th className="px-3 py-2 text-left">Due On</th>
                                <th className="px-3 py-2 text-left">Priority</th>
                                <th className="px-3 py-2 text-center">Required</th>
                                <th className="px-3 py-2 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {assignmentsLoading ? (
                                <tr>
                                  <td
                                    colSpan={8}
                                    className="px-3 py-4 text-sm text-slate-500"
                                  >
                                    Loading assignments...
                                  </td>
                                </tr>
                              ) : filteredAssignments.length ? (
                                filteredAssignments.map((assignment) => {
                                  const isEditing =
                                    editingAssignmentId === assignment.id;
                                  const priorityLabel =
                                    PRIORITY_OPTIONS.find(
                                      (option) =>
                                        option.value === (assignment.priority || "normal")
                                    )?.label || "Normal";
                                  const roleLabel =
                                    ROLE_OPTIONS.find(
                                      (role) => role.value === assignment.target_role
                                    )?.label || assignment.target_role || "—";
                                  const userLabel =
                                    assignment.target_user_name ||
                                    (assignment.target_user_id
                                      ? `User #${assignment.target_user_id}`
                                      : "—");
                                  return (
                                    <tr key={assignment.id}>
                                      <td className="px-3 py-2 capitalize text-slate-600">
                                        {assignment.target_type}
                                      </td>
                                      <td className="px-3 py-2 text-slate-700">
                                        {assignment.target_type === "role"
                                          ? roleLabel
                                          : userLabel}
                                      </td>
                                      <td className="px-3 py-2 text-slate-700">
                                        {assignment.module_title}
                                      </td>
                                      <td className="px-3 py-2 text-slate-600">
                                        {isEditing ? (
                                          <input
                                            type="date"
                                            value={assignmentDraft.assigned_on}
                                            onChange={(event) =>
                                              setAssignmentDraft((prev) => ({
                                                ...prev,
                                                assigned_on: event.target.value,
                                              }))
                                            }
                                            className="h-9 w-full rounded-lg border border-slate-300 px-2 text-xs"
                                          />
                                        ) : (
                                          formatDateOnlyYYYYMMDD(
                                            assignment.assigned_on
                                          ) || "—"
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-slate-600">
                                        {manageSettings?.training_pro_enabled ? (
                                          isEditing ? (
                                            <input
                                              type="date"
                                              value={assignmentDraft.due_on}
                                              onChange={(event) =>
                                                setAssignmentDraft((prev) => ({
                                                  ...prev,
                                                  due_on: event.target.value,
                                                }))
                                              }
                                              className="h-9 w-full rounded-lg border border-slate-300 px-2 text-xs"
                                            />
                                          ) : assignment.due_on ? (
                                            formatDateOnlyYYYYMMDD(assignment.due_on)
                                          ) : (
                                            "—"
                                          )
                                        ) : (
                                          "—"
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-slate-600">
                                        {manageSettings?.training_pro_enabled ? (
                                          isEditing ? (
                                            <select
                                              value={assignmentDraft.priority}
                                              onChange={(event) =>
                                                setAssignmentDraft((prev) => ({
                                                  ...prev,
                                                  priority: event.target.value,
                                                }))
                                              }
                                              className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-xs"
                                            >
                                              {PRIORITY_OPTIONS.map((option) => (
                                                <option
                                                  key={option.value}
                                                  value={option.value}
                                                >
                                                  {option.label}
                                                </option>
                                              ))}
                                            </select>
                                          ) : (
                                            priorityLabel
                                          )
                                        ) : (
                                          "—"
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-center text-slate-600">
                                        {isEditing ? (
                                          <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                                            <input
                                              type="checkbox"
                                              checked={Boolean(
                                                assignmentDraft.is_required
                                              )}
                                              onChange={(event) =>
                                                setAssignmentDraft((prev) => ({
                                                  ...prev,
                                                  is_required: event.target.checked,
                                                }))
                                              }
                                              className="h-4 w-4 text-emerald-600 border-slate-300 rounded"
                                            />
                                            Required
                                          </label>
                                        ) : assignment.is_required ? (
                                          "Yes"
                                        ) : (
                                          "No"
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-right">
                                        {isEditing ? (
                                          <div className="flex items-center justify-end gap-2 text-xs">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleSaveAssignment(assignment)
                                              }
                                              className="rounded-full bg-slate-900 px-3 py-1 text-white font-semibold"
                                            >
                                              Save
                                            </button>
                                            <button
                                              type="button"
                                              onClick={cancelEditAssignment}
                                              className="text-slate-500"
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        ) : (
                                          <div className="flex items-center justify-end gap-3 text-xs">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                startEditAssignment(assignment)
                                              }
                                              className="font-semibold text-slate-700"
                                            >
                                              Edit
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleDeleteAssignment(assignment.id)
                                              }
                                              className="font-semibold text-rose-600"
                                            >
                                              Delete
                                            </button>
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td
                                    colSpan={8}
                                    className="px-3 py-4 text-sm text-slate-500"
                                  >
                                    No assignments found.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    {manageSettings?.training_pro_enabled && (
                      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="px-5 py-4 border-b border-slate-100">
                          <div className="text-lg font-semibold text-slate-900">
                            Training Pro Dashboard
                          </div>
                        </div>
                        <div className="px-5 py-4">
                          {manageOverview?.by_user?.length ? (
                            <div className="overflow-x-auto rounded-2xl border border-slate-200">
                              <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                                  <tr>
                                    <th className="px-3 py-2 text-left">Employee</th>
                                    <th className="px-3 py-2 text-left">Role</th>
                                    <th className="px-3 py-2 text-center">Assigned</th>
                                    <th className="px-3 py-2 text-center">Completed</th>
                                    <th className="px-3 py-2 text-center">Overdue</th>
                                    <th className="px-3 py-2 text-center">Completion</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                  {manageOverview.by_user.map((row) => (
                                    <tr key={row.user_id}>
                                      <td className="px-3 py-2">{row.name}</td>
                                      <td className="px-3 py-2 capitalize">{row.role}</td>
                                      <td className="px-3 py-2 text-center">
                                        {row.assigned_count}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        {row.completed_count}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        {row.overdue_count}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        {row.completion_pct}%
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-sm text-slate-500">
                              No training progress data yet.
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
                      {loadingModule ? (
                        <div className="text-sm text-slate-500">
                          Loading module details...
                        </div>
                      ) : moduleDetail ? (
                        <div className="space-y-4">
                          <div>
                            <div className="text-xl font-semibold text-slate-900">
                              {moduleDetail.module?.title}
                            </div>
                            <div className="text-sm text-slate-500">
                              {moduleDetail.module?.category?.name || "Uncategorized"}
                            </div>
                            {moduleDetail.module?.description && (
                              <p className="mt-2 text-sm text-slate-600">
                                {moduleDetail.module.description}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                            {moduleDetail?.settings?.training_pro_enabled &&
                              moduleDetail?.acknowledgement && (
                                <span className="text-xs uppercase tracking-wide text-slate-500">
                                  Version {moduleDetail.acknowledgement.module_version}
                                </span>
                              )}
                            <span>
                              Completion:{" "}
                              <span className="font-semibold text-slate-900">
                                {moduleProgress?.completion_pct || 0}%
                              </span>
                            </span>
                          </div>
                          {dashboard?.training_pro_enabled &&
                            (selectedAssignment?.due_on || selectedAssignment?.priority) && (
                              <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                                {selectedAssignment?.due_on && (
                                  <span>
                                    Due {formatDateOnlyYYYYMMDD(selectedAssignment.due_on)}
                                  </span>
                                )}
                                {selectedAssignment?.priority && (
                                  <span className="uppercase">
                                    Priority: {selectedAssignment.priority}
                                  </span>
                                )}
                              </div>
                            )}
                          {moduleDetail?.settings?.training_pro_enabled && (
                            <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                              {moduleDetail?.acknowledgement?.needs_acknowledgement && (
                                <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-700">
                                  SOP acknowledgement required
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-slate-500">
                          Select a module to view details.
                        </div>
                      )}
                    </div>
                    {moduleDetail && (
                      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                        <div className="bg-slate-50 px-4 py-2 text-xs uppercase tracking-wide text-slate-500">
                          Lessons
                        </div>
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="px-3 py-2 text-left">Lesson</th>
                              <th className="px-3 py-2 text-left">Type</th>
                              <th className="px-3 py-2 text-left">Status</th>
                              <th className="px-3 py-2 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {lessons.map((lesson) => {
                              const progress = lessonProgressMap.get(lesson.id);
                              const isCompleted = progress?.status === "completed";
                              return (
                                <tr key={lesson.id}>
                                  <td className="px-3 py-2 text-slate-700">
                                    {lesson.title}
                                  </td>
                                  <td className="px-3 py-2 text-slate-500">
                                    {lesson.lesson_type}
                                  </td>
                                  <td className="px-3 py-2 text-slate-500">
                                    {isCompleted ? "Completed" : "Not started"}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <div className="flex flex-wrap items-center justify-end gap-2 text-xs">
                                      {lesson.lesson_type === "pdf" &&
                                        lesson.document?.url && (
                                          <button
                                            type="button"
                                            onClick={() => handleOpenLessonDocument(lesson)}
                                            className="text-slate-700 hover:text-slate-900"
                                          >
                                            Open document
                                          </button>
                                        )}
                                      {lesson.lesson_type === "link" &&
                                        lesson.external_url && (
                                          <a
                                            href={lesson.external_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-slate-700 hover:text-slate-900"
                                          >
                                            Open link
                                          </a>
                                        )}
                                      {lesson.lesson_type === "video" && lesson.video_url && (
                                        <a
                                          href={lesson.video_url}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-slate-700 hover:text-slate-900"
                                        >
                                          Watch video
                                        </a>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleLessonToggle(lesson.id, !isCompleted)
                                        }
                                        className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                      >
                                        {isCompleted ? "Undo" : "Mark complete"}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                            {lessons.length === 0 && (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="px-3 py-4 text-center text-sm text-slate-500"
                                >
                                  No lessons yet.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {moduleDetail?.settings?.training_pro_enabled &&
                      moduleDetail?.settings?.acknowledgements_enabled && (
                        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-2">
                          <div className="text-sm font-semibold text-slate-900">
                            SOP Acknowledgement
                          </div>
                          {moduleDetail?.acknowledgement?.needs_acknowledgement ? (
                            <div className="space-y-3">
                              <p className="text-sm text-slate-600">
                                This module has been updated. Please acknowledge the
                                latest version.
                              </p>
                              <button
                                type="button"
                                onClick={handleAcknowledge}
                                className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                              >
                                I have read &amp; understood
                              </button>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500">
                              Acknowledged on{" "}
                              {moduleDetail?.acknowledgement?.acknowledged_at
                                ? new Date(
                                    moduleDetail.acknowledgement.acknowledged_at
                                  ).toLocaleString()
                                : "N/A"}
                            </p>
                          )}
                        </div>
                      )}
                    {moduleDetail?.settings?.training_pro_enabled &&
                      moduleDetail?.settings?.quizzes_enabled && (
                        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
                          <div className="text-sm font-semibold text-slate-900">
                            Knowledge Check
                          </div>
                          {quizQuestions.length ? (
                            <div className="space-y-4">
                              {quizQuestions.map((question, index) => (
                                <div key={question.id} className="space-y-2">
                                  <div className="text-sm font-semibold text-slate-800">
                                    {index + 1}. {question.prompt}
                                  </div>
                                  <div className="grid gap-2">
                                    {question.choices.map((choice) => (
                                      <label
                                        key={choice.id}
                                        className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700"
                                      >
                                        <input
                                          type="radio"
                                          name={`quiz-${question.id}`}
                                          checked={quizAnswers[question.id] === choice.id}
                                          onChange={() =>
                                            setQuizAnswers((prev) => ({
                                              ...prev,
                                              [question.id]: choice.id,
                                            }))
                                          }
                                          className="h-4 w-4 text-rose-500 border-slate-300"
                                        />
                                        {choice.choice_text}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              ))}
                              <div className="flex flex-wrap items-center gap-3">
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={handleSubmitQuiz}
                                    disabled={quizSubmitting}
                                    className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                                  >
                                    {quizSubmitting ? "Submitting..." : "Submit quiz"}
                                  </button>
                                  <InfoTooltip
                                    text={TRAINING_TOOLTIPS.quiz.submit}
                                    label="Submit quiz"
                                  />
                                </div>
                                {quizStatus && (
                                  <span className="text-sm text-slate-600">
                                    {quizStatus}
                                  </span>
                                )}
                                {moduleProgress?.best_quiz_score != null && (
                                  <span className="text-xs text-slate-500">
                                    Best score: {moduleProgress.best_quiz_score}%
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-slate-500">
                              No quiz questions yet.
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {removeDocConfirmOpen && (
          <DeleteTicketModal
            isOpen={removeDocConfirmOpen}
            onClose={() => {
              if (removeDocSubmitting) return;
              setRemoveDocConfirmOpen(false);
              setRemoveDocLessonId(null);
              setRemoveDocError("");
            }}
            onConfirm={handleConfirmRemoveLessonDocument}
            isSubmitting={removeDocSubmitting}
            title="Remove document?"
            message="This will detach the document from this lesson."
            subMessage="You can upload it again later."
            confirmText="Remove"
            confirmBusyText="Removing..."
            errorText={removeDocError}
          />
        )}
      </div>
    </div>
  );
}
